import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CustomButton from "../CustomButton/CustomButton";
import {
  fetchMeetingAvailability,
  formatMeetingDateLabel,
  formatSlotTimeLabel,
  getBrowserTimezone,
} from "../../services/meetingApi";
import { getApiErrorMessage } from "../../services/apiClient";
import "./MeetingBookingModal.scss";

const MEETING_DURATION_MINUTES = 30;

const toIsoDate = (value) => {
  if (!value) {
    return "";
  }

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const isWeekend = (date) => {
  const day = date.getDay();

  return day === 0 || day === 6;
};

const MeetingBookingModal = ({ show, onHide, onConfirm, initialSelection }) => {
  const timezone = useMemo(() => getBrowserTimezone(), []);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!show) {
      return;
    }

    if (initialSelection?.meetingDate) {
      setSelectedDate(new Date(`${initialSelection.meetingDate}T12:00:00`));
      setSelectedSlot({
        startTime: initialSelection.meetingStartTime,
        endTime: initialSelection.meetingEndTime,
      });
      return;
    }

    setSelectedDate(null);
    setSelectedSlot(null);
    setAvailability(null);
    setLoadError("");
  }, [show, initialSelection]);

  useEffect(() => {
    if (!show || !selectedDate) {
      setAvailability(null);
      return;
    }

    let cancelled = false;

    const loadAvailability = async () => {
      setIsLoadingSlots(true);
      setLoadError("");
      setSelectedSlot(null);

      try {
        const date = toIsoDate(selectedDate);
        const data = await fetchMeetingAvailability(date, timezone);

        if (!cancelled) {
          setAvailability(data);
        }
      } catch (error) {
        if (!cancelled) {
          setAvailability(null);
          setLoadError(
            getApiErrorMessage(error, "Failed to load available meeting slots."),
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoadingSlots(false);
        }
      }
    };

    loadAvailability();

    return () => {
      cancelled = true;
    };
  }, [show, selectedDate, timezone]);

  const availableSlots = (availability?.slots || []).filter(
    (slot) => slot.available,
  );

  const handleConfirm = () => {
    if (!selectedDate || !selectedSlot) {
      window.alert("Please select a date and time slot.");
      return;
    }

    onConfirm?.({
      meetingDate: toIsoDate(selectedDate),
      meetingStartTime: selectedSlot.startTime,
      meetingEndTime: selectedSlot.endTime,
      meetingTimezone: timezone,
    });
    onHide?.();
  };

  const timezoneLabel = useMemo(() => {
    try {
      return new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        timeZoneName: "shortGeneric",
        hour: "numeric",
        minute: "2-digit",
      }).format(new Date());
    } catch {
      return timezone;
    }
  }, [timezone]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="MeetingBookingModal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Schedule a meeting</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="meeting_Booking_Layout">
          <aside className="meeting_Booking_Info">
            <p className="meeting_Booking_Host">Digipank Team</p>
            <h3 className="meeting_Booking_Title">{MEETING_DURATION_MINUTES} Minute Meeting</h3>
            <p className="meeting_Booking_Duration">{MEETING_DURATION_MINUTES} min</p>
            <p className="meeting_Booking_Help">
              Pick a date and time. A video meeting link will be shared after you submit the contact form.
            </p>
          </aside>

          <section className="meeting_Booking_Scheduler">
            <h4 className="meeting_Booking_Subtitle">Select a Date &amp; Time</h4>

            <div className="meeting_Booking_Panels">
              <div className="meeting_Booking_CalendarPanel">
                <DatePicker
                  inline
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  minDate={new Date()}
                  filterDate={(date) => !isWeekend(date)}
                />
                <p className="meeting_Booking_Timezone">
                  Time zone: {timezone} ({timezoneLabel})
                </p>
              </div>

              <div className="meeting_Booking_SlotsPanel">
                {!selectedDate ? (
                  <p className="meeting_Booking_Empty">Select a date to view available times.</p>
                ) : isLoadingSlots ? (
                  <p className="meeting_Booking_Empty">Loading available slots...</p>
                ) : loadError ? (
                  <p className="meeting_Booking_Error">{loadError}</p>
                ) : availability?.isHoliday ? (
                  <p className="meeting_Booking_Empty">This date is a company holiday.</p>
                ) : availability?.isNonWorkingDay ? (
                  <p className="meeting_Booking_Empty">Meetings are available Monday through Friday only.</p>
                ) : availableSlots.length === 0 ? (
                  <p className="meeting_Booking_Empty">No available slots for this date.</p>
                ) : (
                  <>
                    <p className="meeting_Booking_SelectedDate">
                      {formatMeetingDateLabel(toIsoDate(selectedDate))}
                    </p>
                    <div className="meeting_Booking_SlotList">
                      {availability.slots.map((slot) => {
                        const isSelected =
                          selectedSlot?.startTime === slot.startTime &&
                          selectedSlot?.endTime === slot.endTime;

                        return (
                          <button
                            key={`${slot.startTime}-${slot.endTime}`}
                            type="button"
                            className={`meeting_Booking_Slot ${
                              isSelected ? "is-selected" : ""
                            } ${slot.available ? "" : "is-blocked"}`}
                            disabled={!slot.available}
                            onClick={() =>
                              slot.available &&
                              setSelectedSlot({
                                startTime: slot.startTime,
                                endTime: slot.endTime,
                              })
                            }
                          >
                            {formatSlotTimeLabel(slot.startTime)}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <CustomButton title="Cancel" type="button" onClick={onHide} />
        <CustomButton
          title="Confirm slot"
          type="button"
          onClick={handleConfirm}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default MeetingBookingModal;
